import { useState } from "react";

import { faker } from "@faker-js/faker";
import type { Meta, StoryObj } from "@storybook/react";

import { fakeMultiSelectOptions } from "@/__mocks__/multiselect";
import type { FormFieldGroupProps } from "@/ui/composed/form/FormFieldGroup";
import FormFieldGroup from "@/ui/composed/form/FormFieldGroup";
import type { FormFieldProps } from "@/ui/composed/form/types";
import { FormFieldType } from "@/ui/composed/form/types";
import { ScreenContainer } from "@/ui/layout";

const StoryMeta: Meta<FormFieldGroupProps> = {
  title: "Composed/Form/Form Field Group",
  component: FormFieldGroup,
  argTypes: {
    fields: { control: "object" },
    initialValues: { control: "object" },
    onChange: { action: "Changed" },
    onSave: { action: "Saved" },
  },
  args: {
    fields: [
      {
        hasError: false,
        leftText: "Legal first name",
        name: "legalFirstName",
      },
      {
        hasError: false,
        leftText: "Legal last name",
        name: "legalLastName",
      },
      {
        hasError: false,
        leftText: "Preferred first name",
        name: "preferredFirstName",
      },
      {
        hasError: false,
        leftText: "Pronouns",
        name: "pronouns",
        options: fakeMultiSelectOptions(3),
      },
    ],
    heading: "Basic Info",
    validationSchema: {
      legalFirstName: { isRequired: true, type: FormFieldType.Text },
      legalLastName: { isRequired: true, type: FormFieldType.Text },
      preferredFirstName: { isRequired: false, type: FormFieldType.Text },
      pronouns: { isRequired: true, type: FormFieldType.Select },
    },
  },
  decorators: [
    (Story) => (
      <ScreenContainer isScrollView>
        <Story />
      </ScreenContainer>
    ),
  ],
};

type Story = StoryObj<FormFieldGroupProps>;

const Primary: Story = {};

const WithInitialValues: Story = {
  args: {
    initialValues: {
      legalFirstName: faker.person.firstName(),
      legalLastName: faker.person.lastName(),
      preferredFirstName: faker.person.firstName(),
      pronouns: "She/Her/Hers",
    },
  },
};

const WithConditionalField: Story = {
  // eslint-disable-next-line no-restricted-syntax
  render: function Render() {
    const [fieldValues, setFieldValues] = useState<
      Record<string, FormFieldProps["value"]>
    >({});

    const isSameAsShipping = Boolean(fieldValues.isSameAsShipping);

    const validationSchema = {
      isSameAsShipping: { isRequired: false, type: FormFieldType.CheckBox },
      mainAddress: { isRequired: true, type: FormFieldType.Address },
      shippingAddress: {
        isHidden: isSameAsShipping,
        isRequired: true,
        type: FormFieldType.Address,
      },
    };

    const fields = [
      {
        isRequired: true,
        leftText: "Main address",
        name: "mainAddress",
      },
      {
        leftText: "This is also my shipping address",
        name: "isSameAsShipping",
      },
      {
        leftText: "Shipping address",
        name: "shippingAddress",
      },
    ];

    const handleChange = (values: Record<string, FormFieldProps["value"]>) => {
      setFieldValues(values);
    };

    return (
      <ScreenContainer isScrollView>
        <FormFieldGroup
          fields={fields}
          heading="Address"
          initialValues={fieldValues}
          onChange={handleChange}
          onSave={(values) => console.log("Address saved: ", values)}
          testID="conditional-shipping"
          validationSchema={validationSchema}
        />
      </ScreenContainer>
    );
  },
};

export { Primary, WithConditionalField, WithInitialValues };
export default StoryMeta;
